import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest, withApi } from "../utils/api";

// Vacaciones propias (GET /vacations/me), ordenadas de la más reciente a la más antigua.
export const fetchVacations = createAsyncThunk(
  "vacation/fetchVacations",
  withApi(async (_, { token }) => {
    const { data, message } = await apiRequest("/vacations/me", {
      token,
      withMessage: true,
    });
    return {
      message: message || "Vacaciones obtenidas",
      data: Array.isArray(data) ? data : [],
    };
  })
);

// Saldo propio: { accumulatedDays, bonusDays, usedDays, availableDays }.
export const fetchVacationBalance = createAsyncThunk(
  "vacation/fetchVacationBalance",
  withApi((_, { token }) => apiRequest("/vacations/me/balance", { token }))
);

export const createVacation = createAsyncThunk(
  "vacation/createVacation",
  withApi(async ({ startDate, endDate, isBirthdayHalfDay = false }, { token }) => {
    const { data, message } = await apiRequest("/vacations", {
      method: "POST",
      body: {
        dateRange: { startDate, endDate },
        isBirthdayHalfDay: Boolean(isBirthdayHalfDay),
      },
      token,
      withMessage: true,
    });
    return {
      message: message || "Vacaciones registradas",
      data: data || null,
    };
  })
);

// Solo se pueden cambiar las fechas (UpdateVacationDto = { dateRange }).
export const updateVacation = createAsyncThunk(
  "vacation/updateVacation",
  withApi(async ({ id, startDate, endDate }, { token }) => {
    const { data, message } = await apiRequest(`/vacations/${id}`, {
      method: "PATCH",
      body: { dateRange: { startDate, endDate } },
      token,
      withMessage: true,
    });
    return {
      message: message || "Vacaciones actualizadas",
      data: data || null,
    };
  })
);

// Solo vacaciones que aún no han empezado; devuelve los días al saldo.
export const deleteVacation = createAsyncThunk(
  "vacation/deleteVacation",
  withApi(async (id, { token }) => {
    const { message } = await apiRequest(`/vacations/${id}`, {
      method: "DELETE",
      token,
      withMessage: true,
    });
    return { id, message: message || "Vacaciones eliminadas" };
  })
);

const vacationSlice = createSlice({
  name: "vacation",
  initialState: {
    vacations: [],
    statusFetchVacations: "idle",
    errorFetchVacations: null,
    messageFetchVacations: null,
    balance: null,
    statusFetchBalance: "idle",
    errorFetchBalance: null,
    statusCreateVacation: "idle",
    errorCreateVacation: null,
    messageCreateVacation: null,
    statusUpdateVacation: "idle",
    errorUpdateVacation: null,
    messageUpdateVacation: null,
    statusDeleteVacation: "idle",
    errorDeleteVacation: null,
  },
  reducers: {
    clearVacationFeedback: (state) => {
      state.errorFetchVacations = null;
      state.messageFetchVacations = null;
      state.statusFetchVacations = "idle";
      state.errorCreateVacation = null;
      state.messageCreateVacation = null;
      state.statusCreateVacation = "idle";
      state.errorUpdateVacation = null;
      state.messageUpdateVacation = null;
      state.statusUpdateVacation = "idle";
      state.errorDeleteVacation = null;
      state.statusDeleteVacation = "idle";
    },
    setInitialVacations: (state, action) => {
      state.vacations = Array.isArray(action.payload) ? action.payload : [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVacations.pending, (state) => {
        state.statusFetchVacations = "loading";
        state.errorFetchVacations = null;
        state.messageFetchVacations = null;
      })
      .addCase(fetchVacations.fulfilled, (state, action) => {
        state.statusFetchVacations = "succeeded";
        state.errorFetchVacations = null;
        state.messageFetchVacations = action.payload?.message || null;
        state.vacations = Array.isArray(action.payload?.data)
          ? action.payload.data
          : [];
      })
      .addCase(fetchVacations.rejected, (state, action) => {
        state.statusFetchVacations = "failed";
        state.messageFetchVacations = null;
        state.errorFetchVacations =
          action.payload ||
          action.error?.message ||
          "No se pudieron obtener las vacaciones";
      })
      .addCase(fetchVacationBalance.pending, (state) => {
        state.statusFetchBalance = "loading";
        state.errorFetchBalance = null;
      })
      .addCase(fetchVacationBalance.fulfilled, (state, action) => {
        state.statusFetchBalance = "succeeded";
        state.balance = action.payload || null;
      })
      .addCase(fetchVacationBalance.rejected, (state, action) => {
        state.statusFetchBalance = "failed";
        state.errorFetchBalance =
          action.payload || "No se pudo obtener el saldo de vacaciones";
      })
      .addCase(createVacation.pending, (state) => {
        state.statusCreateVacation = "loading";
        state.errorCreateVacation = null;
        state.messageCreateVacation = null;
      })
      .addCase(createVacation.fulfilled, (state, action) => {
        state.statusCreateVacation = "succeeded";
        state.errorCreateVacation = null;
        state.messageCreateVacation = action.payload?.message || null;
        if (action.payload?.data) {
          state.vacations.unshift(action.payload.data);
        }
      })
      .addCase(createVacation.rejected, (state, action) => {
        state.statusCreateVacation = "failed";
        state.messageCreateVacation = null;
        state.errorCreateVacation =
          action.payload || action.error?.message || "No se pudo crear la vacacion";
      })
      .addCase(updateVacation.pending, (state) => {
        state.statusUpdateVacation = "loading";
        state.errorUpdateVacation = null;
        state.messageUpdateVacation = null;
      })
      .addCase(updateVacation.fulfilled, (state, action) => {
        state.statusUpdateVacation = "succeeded";
        state.errorUpdateVacation = null;
        state.messageUpdateVacation = action.payload?.message || null;

        if (action.payload?.data) {
          const updated = action.payload.data;
          const index = state.vacations.findIndex((v) => v._id === updated._id);
          if (index !== -1) {
            state.vacations[index] = updated;
          }
        }
      })
      .addCase(updateVacation.rejected, (state, action) => {
        state.statusUpdateVacation = "failed";
        state.messageUpdateVacation = null;
        state.errorUpdateVacation =
          action.payload ||
          action.error?.message ||
          "No se pudo actualizar la vacacion";
      })
      .addCase(deleteVacation.pending, (state) => {
        state.statusDeleteVacation = "loading";
        state.errorDeleteVacation = null;
      })
      .addCase(deleteVacation.fulfilled, (state, action) => {
        state.statusDeleteVacation = "succeeded";
        state.vacations = state.vacations.filter((v) => v._id !== action.payload.id);
      })
      .addCase(deleteVacation.rejected, (state, action) => {
        state.statusDeleteVacation = "failed";
        state.errorDeleteVacation =
          action.payload || "No se pudieron eliminar las vacaciones";
      });
  },
});

export const { clearVacationFeedback, setInitialVacations } = vacationSlice.actions;
export default vacationSlice.reducer;
